import React, { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  SKILLS,
  POSITION_KEYS,
  EXPERIENCE_KEYS,
  getPositionRange,
  getOverallRange,
  getDominantPosition,
  getPositionBonus,
  getCumulativeExpBonus,
  getPositionCap,
  getExpCap,
} from "data/salaryData";

// ─────────────────────────────────────────────
//  Layout
// ─────────────────────────────────────────────
const PageWrapper = styled.div`
  background: #f7f8fa;
  min-height: 60vh;
  padding: 3rem 0 4rem;

  @media (max-width: 768px) {
    padding: 2rem 0 3rem;
  }
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

// ─────────────────────────────────────────────
//  Page header (Japanese-style clean title)
// ─────────────────────────────────────────────
const Header = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const JapaneseKicker = styled.div`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: #0c4d96;
  margin-bottom: 0.75rem;
  position: relative;
  padding: 0 1.5rem;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 1.25rem;
    height: 1px;
    background: #0c4d96;
  }
  &::before {
    left: -0.25rem;
  }
  &::after {
    right: -0.25rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.01em;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PageSubtitle = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.75;
  max-width: 640px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// ─────────────────────────────────────────────
//  Simulator card — two-column grid
// ─────────────────────────────────────────────
const SimulatorCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e6e8ec;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const CardTopBar = styled.div`
  background: linear-gradient(135deg, #0c4d96 0%, #1a5fa8 100%);
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.85rem 1rem;
  }
`;

const CardTopIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 16px;
    height: 16px;
    stroke: white;
    fill: none;
    stroke-width: 2;
  }
`;

const CardTopText = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const CardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CardSection = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const Divider = styled.div`
  background: #e6e8ec;

  @media (max-width: 900px) {
    height: 1px;
    width: 100%;
  }
`;

// ─────────────────────────────────────────────
//  Form elements
// ─────────────────────────────────────────────
const StepLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #0c4d96;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
`;

const StepTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: 0.01em;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #111827;
  background: #ffffff;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0c4d96;
    box-shadow: 0 0 0 3px rgba(12, 77, 150, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const PositionHelp = styled.p`
  font-size: 0.7rem;
  color: #6b7280;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
`;

const PositionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const PositionItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${(props) => (props.selected ? "#0c4d96" : "#e5e7eb")};
  background: ${(props) => (props.selected ? "#eaf2fc" : "#ffffff")};

  &:hover {
    border-color: #0c4d96;
    background: ${(props) => (props.selected ? "#eaf2fc" : "#f5f8fc")};
  }
`;

const PositionCheckbox = styled.input`
  width: 15px;
  height: 15px;
  accent-color: #0c4d96;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

const PositionLabel = styled.span`
  font-size: 0.78rem;
  color: #374151;
  font-weight: ${(props) => (props.selected ? 600 : 400)};
  line-height: 1.3;
`;

// ─────────────────────────────────────────────
//  Result display — right side
// ─────────────────────────────────────────────
const ResultSection = styled.div`
  background: linear-gradient(180deg, #fafbfc 0%, #f7f8fa 100%);
  padding: 2rem;
  height: 100%;

  @media (max-width: 900px) {
    border-top: 1px solid #e6e8ec;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const ResultLabel = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const ResultHeadline = styled(motion.div)`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  text-align: center;
  margin-bottom: 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0c4d96 0%, #4a90e2 100%);
  }
`;

const ResultCaption = styled.div`
  font-size: 0.78rem;
  color: #4b5563;
  margin-bottom: 0.6rem;
`;

const ResultRange = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem;
  color: #0c4d96;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const RangeNumber = styled.span`
  font-size: 2.4rem;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const RangeSeparator = styled.span`
  font-size: 1.4rem;
  color: #9ca3af;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const RangeUnit = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  margin-left: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const MonthlyNote = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 0.6rem;
`;

const DisclaimerNote = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
  text-align: center;
  margin-top: 0.75rem;
  line-height: 1.5;
`;

// ─────────────────────────────────────────────
//  Per-position breakdown table
// ─────────────────────────────────────────────
const BreakdownTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.6rem;
  letter-spacing: 0.02em;
`;

const BreakdownTable = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  overflow: hidden;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 0.7rem 1rem;
  align-items: center;
  border-bottom: 1px solid #f0f2f5;

  &:last-child {
    border-bottom: none;
  }

  ${(props) =>
    props.dominant &&
    `
    background: #f0f6fd;
  `}
`;

const BreakdownName = styled.div`
  font-size: 0.78rem;
  color: #374151;
  font-weight: ${(props) => (props.dominant ? 700 : 500)};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const DominantBadge = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  color: white;
  background: #0c4d96;
  padding: 2px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
`;

const BreakdownRange = styled.div`
  font-size: 0.78rem;
  color: #0c4d96;
  font-weight: 700;
  white-space: nowrap;
`;

// ─────────────────────────────────────────────
//  Factor breakdown grid
// ─────────────────────────────────────────────
const FactorsTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  margin: 1.25rem 0 0.6rem;
  letter-spacing: 0.02em;
`;

const FactorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
`;

const FactorCell = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
`;

const FactorLabel = styled.div`
  font-size: 0.62rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.2rem;
`;

const FactorValue = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(props) => (props.accent ? "#0c4d96" : "#111827")};
`;

// ─────────────────────────────────────────────
//  Reference table section
// ─────────────────────────────────────────────
const SectionDivider = styled.div`
  margin: 3rem 0 2rem;
  text-align: center;

  @media (max-width: 768px) {
    margin: 2.5rem 0 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const ReferenceCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e6e8ec;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const ReferenceHeader = styled.div`
  padding: 1.15rem 1.5rem;
  background: #fafbfc;
  border-bottom: 1px solid #e6e8ec;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 0.9rem 1rem;
  }
`;

const ReferenceHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const ReferenceTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "";
    width: 3px;
    height: 14px;
    background: #0c4d96;
    border-radius: 2px;
  }
`;

const ReferenceSkillBadge = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #0c4d96;
  background: #e8f2ff;
  padding: 4px 10px;
  border-radius: 4px;
  white-space: nowrap;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
`;

const Th = styled.th`
  font-size: 0.7rem;
  font-weight: 700;
  color: #374151;
  background: #f7f8fa;
  padding: 0.75rem 0.85rem;
  text-align: ${(props) => (props.center ? "center" : "left")};
  border-bottom: 1px solid #e6e8ec;
  white-space: nowrap;
  letter-spacing: 0.02em;
  position: sticky;
  top: 0;

  &:first-child {
    text-align: left;
    background: #f0f2f5;
    position: sticky;
    left: 0;
    z-index: 2;
    border-right: 1px solid #e6e8ec;
  }
`;

const Td = styled.td`
  font-size: 0.72rem;
  color: #374151;
  padding: 0.7rem 0.85rem;
  text-align: ${(props) => (props.center ? "center" : "left")};
  border-bottom: 1px solid #f0f2f5;
  white-space: nowrap;

  &:first-child {
    font-weight: 600;
    color: #111827;
    background: #fafbfc;
    position: sticky;
    left: 0;
    z-index: 1;
    border-right: 1px solid #e6e8ec;
  }
`;

const RangeCell = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  color: ${(props) => (props.highlight ? "#0c4d96" : "#374151")};
  font-weight: ${(props) => (props.highlight ? 700 : 500)};
`;

const TableFootnote = styled.div`
  padding: 0.85rem 1.5rem;
  background: #fafbfc;
  border-top: 1px solid #e6e8ec;
  font-size: 0.7rem;
  color: #6b7280;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 0.85rem 1rem;
  }
`;

// ─────────────────────────────────────────────
//  Icons
// ─────────────────────────────────────────────
const CalculatorIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="10" y2="14" />
    <line x1="8" y1="18" x2="10" y2="18" />
    <line x1="14" y1="14" x2="16" y2="14" />
    <line x1="14" y1="18" x2="16" y2="18" />
  </svg>
);

// ─────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────
export default function SalaryPageContent() {
  const { t } = useTranslation();

  const [skillId, setSkillId] = useState(SKILLS[0].id);
  const [experience, setExperience] = useState("1-2");
  const [selectedPositions, setSelectedPositions] = useState(["dd"]);

  const skill = useMemo(
    () => SKILLS.find((s) => s.id === skillId) || SKILLS[0],
    [skillId]
  );

  const handlePositionChange = useCallback((positionKey, checked) => {
    setSelectedPositions((prev) => {
      if (checked) {
        if (prev.includes(positionKey)) return prev;
        return [...prev, positionKey];
      }
      // Prevent unchecking the last selected position
      if (prev.length <= 1) return prev;
      return prev.filter((p) => p !== positionKey);
    });
  }, []);

  const overall = useMemo(
    () => getOverallRange(skill, experience, selectedPositions),
    [skill, experience, selectedPositions]
  );

  const dominant = useMemo(
    () => getDominantPosition(skill, selectedPositions),
    [skill, selectedPositions]
  );

  const positionRanges = useMemo(() => {
    return selectedPositions.map((key) => ({
      key,
      range: getPositionRange(skill, experience, key),
    }));
  }, [skill, experience, selectedPositions]);

  const factors = useMemo(() => {
    if (!dominant) return null;
    const posBonus = getPositionBonus(skill, dominant);
    const expBonus = getCumulativeExpBonus(skill, experience);
    const posCap = getPositionCap(skill, dominant);
    const expCap = getExpCap(skill, experience);
    const effectiveCap = Math.min(posCap, expCap);
    return { base: skill.base, posBonus, expBonus, effectiveCap };
  }, [skill, experience, dominant]);

  // Group skills by category for the dropdown
  const skillGroups = useMemo(() => {
    const groups = {};
    SKILLS.forEach((s) => {
      const cat = s.categoryKey;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return groups;
  }, []);

  const monthlyMin = overall ? Math.round(overall.min / 12) : 0;
  const monthlyMax = overall ? Math.round(overall.max / 12) : 0;

  return (
    <PageWrapper>
      <Container>
        {/* Page header */}
        <Header>
          <JapaneseKicker>{t("salary_page.kicker")}</JapaneseKicker>
          <PageTitle>{t("salary_page.title")}</PageTitle>
          <PageSubtitle>{t("salary_page.subtitle")}</PageSubtitle>
        </Header>

        {/* Simulator */}
        <SimulatorCard>
          <CardTopBar>
            <CardTopIcon>
              <CalculatorIcon />
            </CardTopIcon>
            <CardTopText>{t("salary_page.simulator_title")}</CardTopText>
          </CardTopBar>

          <CardBody>
            {/* LEFT: Inputs */}
            <CardSection>
              <FormGroup>
                <StepLabel>
                  <StepNumber>1</StepNumber>
                  <StepTitle>{t("salary_page.step1_skill")}</StepTitle>
                </StepLabel>
                <Select
                  value={skillId}
                  onChange={(e) => setSkillId(e.target.value)}
                >
                  {Object.entries(skillGroups).map(([catKey, skills]) => (
                    <optgroup key={catKey} label={t(catKey)}>
                      {skills.map((s) => (
                        <option key={s.id} value={s.id}>
                          {t(s.nameKey)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <StepLabel>
                  <StepNumber>2</StepNumber>
                  <StepTitle>{t("salary_page.step2_experience")}</StepTitle>
                </StepLabel>
                <Select
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                >
                  {EXPERIENCE_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {t(`salary_calculator.experience.${key}`)}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <StepLabel>
                  <StepNumber>3</StepNumber>
                  <StepTitle>{t("salary_page.step3_position")}</StepTitle>
                </StepLabel>
                <PositionHelp>{t("salary_page.position_help")}</PositionHelp>
                <PositionGrid>
                  {POSITION_KEYS.map((key) => {
                    const selected = selectedPositions.includes(key);
                    return (
                      <PositionItem key={key} selected={selected}>
                        <PositionCheckbox
                          type="checkbox"
                          checked={selected}
                          onChange={(e) =>
                            handlePositionChange(key, e.target.checked)
                          }
                        />
                        <PositionLabel selected={selected}>
                          {t(`salary_calculator.positions.${key}`)}
                        </PositionLabel>
                      </PositionItem>
                    );
                  })}
                </PositionGrid>
              </FormGroup>
            </CardSection>

            <Divider />

            {/* RIGHT: Results */}
            <ResultSection>
              <ResultLabel>{t("salary_page.result_label")}</ResultLabel>

              <ResultHeadline
                key={`${skill.id}-${experience}-${selectedPositions.join(",")}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ResultCaption>{t("salary_page.result_caption")}</ResultCaption>
                {overall && (
                  <>
                    <ResultRange>
                      <RangeNumber>{overall.min}</RangeNumber>
                      <RangeSeparator>〜</RangeSeparator>
                      <RangeNumber>{overall.max}</RangeNumber>
                      <RangeUnit>{t("salary_calculator.unit")}</RangeUnit>
                    </ResultRange>
                    <MonthlyNote>
                      {t("salary_page.monthly_equivalent", {
                        min: monthlyMin,
                        max: monthlyMax,
                      })}
                    </MonthlyNote>
                  </>
                )}
              </ResultHeadline>

              {/* Per-position breakdown */}
              {positionRanges.length > 0 && (
                <>
                  <BreakdownTitle>
                    {t("salary_page.per_position_breakdown")}
                  </BreakdownTitle>
                  <BreakdownTable>
                    {positionRanges.map(({ key, range }) => (
                      <BreakdownRow key={key} dominant={key === dominant}>
                        <BreakdownName dominant={key === dominant}>
                          {t(`salary_calculator.positions.${key}`)}
                          {key === dominant && (
                            <DominantBadge>
                              {t("salary_page.dominant_badge")}
                            </DominantBadge>
                          )}
                        </BreakdownName>
                        <BreakdownRange>
                          {range.min} 〜 {range.max}
                        </BreakdownRange>
                      </BreakdownRow>
                    ))}
                  </BreakdownTable>
                </>
              )}

              {/* Calculation factors */}
              {factors && (
                <>
                  <FactorsTitle>{t("salary_page.factors_title")}</FactorsTitle>
                  <FactorsGrid>
                    <FactorCell>
                      <FactorLabel>{t("salary_page.factors.base")}</FactorLabel>
                      <FactorValue>{factors.base}</FactorValue>
                    </FactorCell>
                    <FactorCell>
                      <FactorLabel>
                        {t("salary_page.factors.position_bonus")}
                      </FactorLabel>
                      <FactorValue accent>+{factors.posBonus}</FactorValue>
                    </FactorCell>
                    <FactorCell>
                      <FactorLabel>
                        {t("salary_page.factors.experience_bonus")}
                      </FactorLabel>
                      <FactorValue accent>+{factors.expBonus}</FactorValue>
                    </FactorCell>
                    <FactorCell>
                      <FactorLabel>
                        {t("salary_page.factors.effective_cap")}
                      </FactorLabel>
                      <FactorValue>{factors.effectiveCap}</FactorValue>
                    </FactorCell>
                  </FactorsGrid>
                </>
              )}

              <DisclaimerNote>{t("salary_page.disclaimer")}</DisclaimerNote>
            </ResultSection>
          </CardBody>
        </SimulatorCard>

        {/* Reference table — full salary range matrix for the selected skill */}
        <SectionDivider>
          <SectionTitle>{t("salary_page.reference_title")}</SectionTitle>
          <SectionDescription>
            {t("salary_page.reference_subtitle")}
          </SectionDescription>
        </SectionDivider>

        <ReferenceCard>
          <ReferenceHeader>
            <ReferenceHeaderLeft>
              <ReferenceTitle>
                {t("salary_page.reference_table_title")}
              </ReferenceTitle>
            </ReferenceHeaderLeft>
            <ReferenceSkillBadge>{t(skill.nameKey)}</ReferenceSkillBadge>
          </ReferenceHeader>

          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t("salary_page.position_label")}</Th>
                  {EXPERIENCE_KEYS.map((expKey) => (
                    <Th key={expKey} center>
                      {t(`salary_calculator.experience.${expKey}`)}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {POSITION_KEYS.map((posKey) => (
                  <tr key={posKey}>
                    <Td>{t(`salary_calculator.positions.${posKey}`)}</Td>
                    {EXPERIENCE_KEYS.map((expKey) => {
                      const range = getPositionRange(skill, expKey, posKey);
                      const isCurrent =
                        expKey === experience &&
                        selectedPositions.includes(posKey);
                      return (
                        <Td key={expKey} center>
                          <RangeCell highlight={isCurrent}>
                            {range.min}〜{range.max}
                          </RangeCell>
                        </Td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>

          <TableFootnote>{t("salary_page.reference_footnote")}</TableFootnote>
        </ReferenceCard>
      </Container>
    </PageWrapper>
  );
}
